import {
  SelectInput,
  ReferenceInput,
  SimpleFormIterator,
  ArrayInput,
  SimpleForm,
  useSimpleFormIteratorItem,
  FormDataConsumer,
  useTranslate,
  ReferenceArrayInput,
  SelectArrayInput,
  useRedirect,
  useNotify,
  AutocompleteInput,
  useRecordContext,
  useRefresh,
  useLoading,
} from "react-admin";
import { Grid, Typography } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import React, { useState, useEffect, useCallback } from "react";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import dataProvider from "server/dataProvider";
import { CampaignSubTypes, ClassTypes, PRELIMINARY } from "utils/constants";
import { candidateRequests } from "api/candidateApi";
import CustomDateTimeInput from "server/components/Fields/CustomDateTimeInput";
import ExamReOrderButtons from "./ExamReOrderButtons";
import ExamList from "../../components/ExamList";
import Declarations from "components/Campaigns/components/Declarations";
import NamesInput from "components/Campaigns/components/Inputs/NamesInput";
import CitizenshipInput from "components/Campaigns/components/Inputs/CitizenshipInput";
import CampaignTypeInput from "components/Campaigns/components/Inputs/CampaignTypeInput";
import DirectionInput from "components/Campaigns/components/Inputs/DirectionInput";
import Taxes from "components/Campaigns/components/Taxes";
import { settings } from "app";
import AdmissionMarkerInput from "components/Campaigns/components/Inputs/AdmissionMarkerInput";
import InactiveDeleteOptionEditToolbar from "features/EditToolbar";
import { paymentRequests } from "api/paymentApi";
import { useNotifyError } from "utils/useNotifyError";

const validateExamDate = (value, values) => {
  if (!value) {
    return "Полето е задължително";
  }

  const valueRegistrationTo = values.registrationTo;
  if (valueRegistrationTo && valueRegistrationTo >= value) {
    return "Датата на изпита трябва да е след датата за край на кампания";
  }

  const dateMap = new Map();
  Object.keys(values).forEach(key => {
    if (key.startsWith("examTimestamp")) {
      const timestamp = values[key];
      const dateOnly = timestamp?.split("T")[0];
      if (dateOnly) {
        if (dateMap.has(dateOnly)) {
          dateMap.set(dateOnly, false);
          return;
        } else {
          dateMap.set(dateOnly, true);
        }
      }
    }
  });

  const valueStartTime = value?.split("T")[0];
  if (dateMap.has(valueStartTime)) {
    if (!dateMap.get(valueStartTime)) {
      return "Датите на изпитите трябва да са в различни дни";
    }
  }
};

const validateStartDate = (value, allValues) => {
  if (!value) {
    return "Полето е задължително";
  }
  if (value >= allValues.registrationTo) {
    return "Началната дата трябва да е по-малка от крайната";
  }
};

const validateEndDate = (value, allValues) => {
  if (!value) {
    return "Полето е задължително";
  }
  if (value <= allValues.registrationFrom) {
    return "Крайната дата трябва да е по-голяма от началната";
  }
};

const formatDate = date => {
  const correctTimeZoneOffset = 2 * 60;
  date = new Date(date);
  date.setMinutes(date.getMinutes() + correctTimeZoneOffset);

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  const milliseconds = String(date.getUTCMilliseconds()).padStart(7, "0");
  const timezoneOffset = "+02:00";

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${timezoneOffset}`;
};

const EditSaveForm = ({ isEdit }) => {
  const record = useRecordContext();

  const redirect = useRedirect();
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();
  const loading = useLoading() || !record;

  const campaignIsStarted = record?.registrationFrom ? new Date(record.registrationFrom) <= new Date() : false;

  const campaignSave = async data => {
    if (examsAreLoading) {
      notify("Изчакайте всички данни да се заредят", { type: "warning" });
      return;
    }
    if (!campaignIsStarted) {
      data.registrationFrom = formatDate(data.registrationFrom);
    }
    data.registrationTo = formatDate(data.registrationTo);

    data.examDtos = exams.map((item, index) => ({
      examId: item.examId,
      ...(item.campaignExamId ? { campaignExamId: item.campaignExamId } : {}),
      examTimestamp: formatDate(data[`examTimestamp${index}`]),
      rooms: data.rooms.map((room, roomIndex) => ({
        roomId: room.roomId,
        priority: ++roomIndex,
      })),
    }));

    try {
      await dataProvider.update(candidateRequests.campaignByType(PRELIMINARY), { id: campaignId, data: data });
      notify("Успешно редактиране", { type: "success" });
      redirect("list", candidateRequests.campaignByType(PRELIMINARY));
      refresh();
    } catch (error) {
      notify(`Error: ${error.message || "Грешка при редактиране"}`, { type: "error" });
    }
  };

  const [minEndDate, setMinEndDate] = useState(record?.RegistrationFrom || null);
  const handleStartDateChange = value => {
    setMinEndDate(value?._i || null);
  };

  const [excludedSpecialityIds, setExcludedSpecialityIds] = useState(() => record?.specialitiesIds | []);
  const [excludedRoomsIds, setExcludedRoomsIds] = useState(record?.rooms?.map(room => room.roomId) || []);
  const [directionId, setDirectionId] = useState(() => record?.directionId || null);

  const [exams, setExams] = useState(record?.exams || []);
  const [examsAreLoading, setExamsAreLoading] = useState(true);

  const [roomsCapacity, setRoomsCapacity] = useState(null);
  const [campaignId] = useState(record?.id);
  const [getExamCampaingIds, setExamCampaingIds] = useState([]);
  const [citizenshipIds, setCitizenshipIds] = useState(() => record?.citizenship || []);

  useEffect(() => {
    const examCampainsNew = [];

    const fetchExams = async () => {
      try {
        const { data } = await dataProvider.customRequest(administrationRequests.getExamsSelect(), null, "GET");
        // Process exams with the fetched data if necessary
        const updatedExams =
          record?.exams?.map((exam, index) => {
            examCampainsNew.push({ campaignExamId: exam.campaignExamId, examId: exam.examId });
            const matchedExam = data.find(d => d.id === exam.examId);
            return {
              campaignExamId: exam.campaignExamId,
              examId: exam.examId,
              name: matchedExam.name,
              examTimestamp: exam.examTimestamp,
              examPassed: exam.examPassed,
              [`examTimestamp${index}`]: exam.examTimestamp,
            };
          }) || [];

        setExamCampaingIds(examCampainsNew);
        setExams(updatedExams);
      } catch (error) {
        console.error("Error fetching exams", error);
      } finally {
        setExamsAreLoading(false);
      }
    };

    fetchExams();
  }, [record?.exams]);

  const fetchRoomsCapacity = useCallback(
    async (selectedRoomsIds, classTypes, showExamCapacity) => {
      if (selectedRoomsIds.length === 0) {
        setRoomsCapacity(null);
        return;
      }

      try {
        const { data } = await dataProvider.customRequest(
          administrationRequests.getRoomsCapacitiesSelect(selectedRoomsIds, classTypes, showExamCapacity),
          null,
          "GET",
        );

        setRoomsCapacity(data);
      } catch (error) {
        notifyError(error);
      }
    },
    [notifyError],
  );

  useEffect(() => {
    if (excludedRoomsIds.length > 0) {
      fetchRoomsCapacity(excludedRoomsIds, 1, true);
    }
  }, [excludedRoomsIds, fetchRoomsCapacity]);

  const citizenshipChange = event => {
    setCitizenshipIds(event.target.value);
  };

  const handleSpecialityChange = async (event, formDataSpecialities) => {
    const selectedSpecialitesIds = formDataSpecialities
      ? formDataSpecialities.map(item => item.specialityId).filter(Boolean)
      : [];
    fetchExamSpecialities(selectedSpecialitesIds);
  };

  const handleRemoveSpeciality = removedId => {
    const updatedSpecialityIdsIds = excludedSpecialityIds.filter(id => id !== removedId);
    fetchExamSpecialities(updatedSpecialityIdsIds);
  };

  const handleRoomChange = async (event, formDataRooms, classTypes, showExamCapacity) => {
    const selectedRoomsIds = formDataRooms ? formDataRooms.map(item => item.roomId).filter(Boolean) : [];
    setExcludedRoomsIds(selectedRoomsIds);

    fetchRoomsCapacity(selectedRoomsIds, classTypes, showExamCapacity);
  };

  const fetchExamSpecialities = async selectedSpecialityIds => {
    setExcludedSpecialityIds(selectedSpecialityIds);
    if (!selectedSpecialityIds?.length) {
      setExams([]);
      return;
    }
    try {
      const { data } = await dataProvider.customRequest(
        administrationRequests.getExamSpecialitiesSelect(selectedSpecialityIds),
        null,
        "GET",
      );
      const newExams = data.map(item => {
        // Find the exam in `data` that matches the examId from `record.exams`
        const matchedExamByCampaignId = getExamCampaingIds.find(e => e.examId === item.id);
        if (matchedExamByCampaignId) {
          return {
            campaignExamId: matchedExamByCampaignId.campaignExamId,
            examId: item.id,
            name: item.name,
          };
        } else {
          return {
            examId: item.id,
            name: item.name,
          };
        }
      });
      setExams(newExams);
    } catch (error) {
      notifyError(error);
    }
  };

  const handleDirectionChange = newDirectionId => {
    setDirectionId(newDirectionId);
  };

  const handleRemoveRoom = async (removedId, classTypes, showExamCapacity) => {
    setExcludedRoomsIds(prevIds => prevIds.filter(id => id !== removedId));

    const updatedExcludedRoomsIds = excludedRoomsIds.filter(id => id !== removedId);

    fetchRoomsCapacity(updatedExcludedRoomsIds, classTypes, showExamCapacity);
  };

  const CustomRemoveButton = ({ record, classTypes, showExamCapacity }) => {
    const translate = useTranslate();
    const { remove } = useSimpleFormIteratorItem();

    if (campaignIsStarted) {
      return;
    }
    const handleClick = () => {
      if (record.specialityId) {
        handleRemoveSpeciality(record.specialityId);
      }
      if (record.roomId) {
        handleRemoveRoom(record.roomId, classTypes, showExamCapacity);
      }
      remove();
    };
    const title = translate("ra.action.remove");
    return (
      <Tooltip title={title}>
        <IconButton
          color="warning"
          onClick={handleClick}
        >
          <RemoveCircleOutlineIcon />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <SimpleForm
      onSubmit={campaignSave}
      toolbar={<InactiveDeleteOptionEditToolbar />}
    >
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
        >
          {isEdit && campaignIsStarted ? (
            <Typography
              color={theme => theme.palette.success.main}
              sx={{ textAlign: "center", fontWeight: "bold" }}
            >
              Кампанията е стартирала
            </Typography>
          ) : (
            <Typography
              color={theme => theme.palette.success.main}
              sx={{ textAlign: "center", fontWeight: "bold" }}
            >
              Кампанията предстои да стартира
            </Typography>
          )}
        </Grid>
        <Grid
          item
          xs={12}
        >
          <AdmissionMarkerInput readOnly={campaignIsStarted || loading} />
        </Grid>
        <Grid
          item
          xs={8}
        >
          <NamesInput readOnly={loading && isEdit} />
        </Grid>
        <Grid
          item
          xs={4}
        >
          <CitizenshipInput
            isArray
            handleChange={citizenshipChange}
            isBgAndForeignersSeperated
            readOnly={campaignIsStarted || (loading && isEdit)}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={2.3}
        >
          <CustomDateTimeInput
            label="Начало на регистрация"
            source="registrationFrom"
            required={true}
            validate={validateStartDate}
            onChange={event => {
              handleStartDateChange(event);
            }}
            defaultValue={record?.minEndDate}
            readOnly={campaignIsStarted || (loading && isEdit)}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={2.3}
        >
          <CustomDateTimeInput
            label="Край на регистрация"
            source="registrationTo"
            validate={validateEndDate}
            inputProps={{
              min: minEndDate,
            }}
            required={true}
            readOnly={loading && isEdit}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <CampaignTypeInput
            subTypeId={CampaignSubTypes.Preliminary}
            readOnly={campaignIsStarted || (loading && isEdit)}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <DirectionInput
            required
            handleChange={handleDirectionChange}
            filterByUser
            filterOnlyForCampaigns
            readOnly={campaignIsStarted || (loading && isEdit)}
          />
        </Grid>
        {directionId && (
          <>
            <Grid
              item
              xs={12}
              md={12}
            >
              <Typography color={"primary"}>Специалности</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
            >
              <ArrayInput
                source="specialityDtos"
                label=" "
                defaultValue={[]}
              >
                <SimpleFormIterator
                  disableReordering
                  disableRemove
                  disableAdd={campaignIsStarted}
                  inline
                  sx={settings.style.iteratorWithoutGap}
                >
                  <FormDataConsumer>
                    {({ scopedFormData, formData }) => {
                      return (
                        <Grid
                          container
                          spacing={2}
                        >
                          <Grid
                            item
                            xs={12}
                            md={11}
                          >
                            <ReferenceInput
                              source="specialityId"
                              reference={administrationRequests.getSpecialitiesSelect()}
                              filter={{
                                excludeIds: excludedSpecialityIds,
                                directionId: directionId,
                                filterByUser: true,
                              }}
                            >
                              <SelectInput
                                label="Специалност"
                                defaultValue={scopedFormData.specialityId}
                                onChange={event => handleSpecialityChange(event, formData.specialityDtos)}
                                readOnly={campaignIsStarted || (loading && isEdit)}
                              />
                            </ReferenceInput>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={1}
                          >
                            <CustomRemoveButton
                              record={scopedFormData} // Pass the current record
                            />
                          </Grid>
                        </Grid>
                      );
                    }}
                  </FormDataConsumer>
                </SimpleFormIterator>
              </ArrayInput>
            </Grid>
            <ExamList
              exams={exams}
              validateExamDate={validateExamDate}
              readOnly={campaignIsStarted || (loading && isEdit)}
            />
            <Grid
              item
              container
            >
              <Grid
                item
                xs={12}
                md={12}
              >
                <Typography color={"primary"}>Зали</Typography>
                <ArrayInput
                  fullWidth
                  source="rooms"
                  label=""
                  defaultValue={[{}]}
                >
                  <SimpleFormIterator
                    inline
                    sx={settings.style.iteratorWithoutGap}
                    reOrderButtons={!campaignIsStarted && <ExamReOrderButtons />}
                    disableAdd={campaignIsStarted}
                    getItemLabel={index => (
                      <Typography
                        noWrap
                        style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}
                      >
                        Приоритет {index + 1}
                      </Typography>
                    )}
                    disableRemove={record => record?.candidatesCount > 0 || campaignIsStarted} // Dynamically disable remove button
                  >
                    <FormDataConsumer>
                      {({ scopedFormData, formData }) => {
                        return (
                          <Grid
                            container
                            spacing={1}
                          >
                            <Grid
                              item
                              xs={12}
                              md={10}
                            >
                              <ReferenceInput
                                source="roomId"
                                label="Зала"
                                reference={administrationRequests.getRoomsSelect()}
                                filter={{
                                  ClassTypes: ClassTypes.Csc,
                                  excludeIds: excludedRoomsIds,
                                  filterByUser: true,
                                }}
                              >
                                <AutocompleteInput
                                  label="Зала"
                                  optionValue="id"
                                  disableClearable
                                  readOnly={
                                    scopedFormData?.candidatesCount > 0 || campaignIsStarted || (loading && isEdit)
                                  }
                                  optionText={rec =>
                                    `${rec.roomName} - ${rec.buildingName}${
                                      rec.capacityExam ? ` (${rec.capacityExam})` : ""
                                    }`
                                  }
                                  filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                                  onChange={event => handleRoomChange(event, formData.rooms, 1, true)}
                                  fullWidth
                                />
                              </ReferenceInput>
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              md={2}
                            >
                              <CustomRemoveButton
                                record={scopedFormData}
                                classTypes={ClassTypes.Csc}
                                showExamCapacity={true}
                              />
                            </Grid>
                          </Grid>
                        );
                      }}
                    </FormDataConsumer>
                  </SimpleFormIterator>
                </ArrayInput>
              </Grid>
            </Grid>
            {roomsCapacity && (
              <Grid
                item
                xs={12}
                md={5}
              >
                <Typography color={"secondary"}>Общ капацитет на залите: {roomsCapacity}</Typography>
              </Grid>
            )}
          </>
        )}
        <Declarations
          citizenshipIds={citizenshipIds}
          isDisabled={campaignIsStarted || (loading && isEdit)}
        />
        <Taxes
          campaignSubTypeId={CampaignSubTypes.Preliminary}
          citizenshipIds={citizenshipIds}
          examsCount={exams?.length || 0}
          specialityIds={excludedSpecialityIds || []}
        />
        <Grid
          item
          md={5}
          xs={12}
        >
          <ReferenceArrayInput
            source="ibanIds"
            reference={paymentRequests.getIbansSelect()}
          >
            <SelectArrayInput
              optionText="name"
              label="Iban"
              fullWidth
              readOnly={loading && isEdit}
            />
          </ReferenceArrayInput>
        </Grid>
      </Grid>
    </SimpleForm>
  );
};
export default EditSaveForm;
